/**
 * Protocol Text Utilities
 *
 * This module provides reusable functions for parsing and validating protocol text
 * used in training programming. It supports various protocol formats like:
 * - RAMP: "ramp 3x4 @8"
 * - SETS_REPS_RPE: "3x4 @5"
 * - SINGLE: "single @5"
 * - REPS_AND_RPE: "x4 @5"
 * - RPE_PROGRESSION: "x3 @4-4-5"
 * - WEIGHT_PROGRESSION: "175-180-185 x3"
 * - SETS_REPS_WEIGHT: "3x5x145"
 *
 * Chaining is supported with "+" separator: "ramp 3x4 @7 + single @7"
 */

import { ProtocolCases } from "../../Resources/Constants";

/**
 * Clamps an RPE value to the valid range [1, 10]
 * @param {number} val - The RPE value to clamp
 * @returns {number} The clamped RPE value
 */
const clampRpe = (val) => Math.min(Math.max(val, 1), 10);

/**
 * Normalizes text by removing all whitespace and converting to lowercase
 * @param {string} text - The text to normalize
 * @returns {string} The normalized text
 */
const normalizeText = (text) => (text || "").replace(/\s+/g, "").toLowerCase();

/**
 * Tests if a given text matches a specific protocol case pattern
 *
 * @param {string} protocolCase - One of ProtocolCases values (e.g., ProtocolCases.RAMP)
 * @param {string} text - The text to test (will be normalized internally)
 * @returns {boolean} True if the text matches the protocol case pattern
 *
 * @example
 * matchProtocolCase(ProtocolCases.RAMP, "ramp 3x4 @8") // true
 * matchProtocolCase(ProtocolCases.SETS_REPS_RPE, "3x4@5") // true
 * matchProtocolCase(ProtocolCases.WEIGHT_PROGRESSION, "175-180-185x3") // true
 */
export function matchProtocolCase(protocolCase, text) {
  const normalized = normalizeText(text);

  switch (protocolCase) {
    case ProtocolCases.RPE_PROGRESSION:
      // matches "x3@4-4-5"
      return /^x\d+@(\d+(\.\d+)?)(-\d+(\.\d+)?)+$/.test(normalized);
    case ProtocolCases.WEIGHT_PROGRESSION:
      // matches "175-180-185x3"
      return /^\d+(-\d+)+x\d+$/.test(normalized);
    case ProtocolCases.RAMP:
      // matches "ramp3x4@8"
      return /^ramp\d+x\d+@\d+(\.\d+)?$/.test(normalized);
    case ProtocolCases.SETS_REPS_RPE:
      // matches "3x4@5"
      return /^\d+x\d+@\d+(\.\d+)?$/.test(normalized);
    case ProtocolCases.SETS_REPS_WEIGHT:
      // matches "<sets>x<reps>x<weight>", "3x5x145"
      return /^\d+x\d+x\d+(?:\.\d+)?$/.test(normalized);
    case ProtocolCases.REPS_AND_RPE:
      // matches "x4@5"
      return /^x\d+@\d+(\.\d+)?$/.test(normalized);
    case ProtocolCases.SINGLE:
      // matches "single@5"
      return /^single@\d+(\.\d+)?$/.test(normalized);
    default:
      return false;
  }
}

/**
 * Detects which protocol case a text segment matches
 *
 * @param {string} text - The text segment to analyze
 * @returns {string|null} The matching ProtocolCase value or null if no match
 *
 * @example
 * detectProtocolCase("3x4 @5") // ProtocolCases.SETS_REPS_RPE
 * detectProtocolCase("ramp 3x4 @8") // ProtocolCases.RAMP
 * detectProtocolCase("invalid") // null
 */
export function detectProtocolCase(text) {
  for (const protocolCase of Object.values(ProtocolCases)) {
    if (matchProtocolCase(protocolCase, text)) {
      return protocolCase;
    }
  }
  return null;
}

/**
 * Validates if a protocol text string is valid (all segments match known patterns)
 *
 * @param {string} text - The protocol text to validate
 * @returns {boolean} True if the text is invalid (has errors), false if valid
 *
 * @example
 * hasProtocolTextError("3x4 @5") // false (valid)
 * hasProtocolTextError("3x4 @5 + single @7") // false (valid chain)
 * hasProtocolTextError("invalid text") // true (error)
 * hasProtocolTextError("") // false (empty is considered valid)
 */
export function hasProtocolTextError(text) {
  if (!text) return false;

  // Split on '+', trim segments, ignore empty
  const parts = text
    .split("+")
    .map((p) => p.trim())
    .filter((p) => p);

  // If any segment does not match any protocol case, flag error
  return !parts.every((part) => detectProtocolCase(part) !== null);
}

/**
 * Generates autocomplete suggestions based on the current input
 *
 * @param {string} input - The current protocol text input
 * @returns {string} The suggested text to append, or empty string if no suggestion
 *
 * @example
 * getProtocolSuggestion("3") // "x4 @5"
 * getProtocolSuggestion("3x4") // " @5"
 * getProtocolSuggestion("ramp") // " 3x4 @7"
 * getProtocolSuggestion("3x4 @5") // " + " (chain suggestion)
 */
export function getProtocolSuggestion(input) {
  if (!input) {
    return "";
  }

  // Split out the last segment after any "+"
  const parts = input.split("+");
  const lastSegment = parts[parts.length - 1].trim();
  // Collapse any internal whitespace to single spaces for pattern matching
  const collapsedSegment = lastSegment.replace(/\s+/g, " ");

  // Suggest weight progression until three values are entered, then suggest reps
  // Suggest weight progression once a dash is typed after a number
  const weightProgressMatch = lastSegment.match(/^(\d+)(?:-(?:\d*)?)+$/);
  if (weightProgressMatch) {
    const baseVal = weightProgressMatch[1];
    const additionalMatches = lastSegment.match(/-(\d+)/g) || [];
    const weightVal =
      additionalMatches.length > 0
        ? additionalMatches[additionalMatches.length - 1].slice(1)
        : baseVal;
    const typedCount = 1 + additionalMatches.length;
    const missing = 3 - typedCount;
    if (missing > 0) {
      const fill = Array(missing).fill(weightVal).join("-");
      // If user just typed a dash, append all remaining without leading dash
      if (lastSegment.endsWith("-")) {
        return fill + " x4";
      }
      // Otherwise prefix with a dash to complete all remaining
      return "-" + fill + " x4";
    } else {
      // All weight values entered, suggest reps
      return " x4";
    }
  }

  // Trigger RPE progression only once a dash is present (e.g. x3@4-, x3@4-5)
  const rpeProgressMatch = lastSegment.match(
    /^x(\d+)\s*@(\d+)(?:-(?:\d+)?)+$/i
  );
  if (rpeProgressMatch) {
    const baseVal = rpeProgressMatch[2];
    // Count how many RPE values have been typed (initial plus subsequent)
    const additionalMatches = lastSegment.match(/-(\d+)/g) || [];
    // Choose the last typed RPE if available, otherwise use the base RPE
    const rpeVal =
      additionalMatches.length > 0
        ? additionalMatches[additionalMatches.length - 1].slice(1)
        : baseVal;
    const typedCount = 1 + additionalMatches.length;
    const missing = 3 - typedCount;
    if (missing > 0) {
      const fill = Array(missing).fill(rpeVal).join("-");
      // When user ends with a dash, append all remaining without leading dash
      if (lastSegment.endsWith("-")) {
        return fill;
      }
      // Otherwise prefix with a dash to complete all remaining
      return "-" + fill;
    }
  }

  // If user types "<sets>x<reps>x", suggest default weight "100"
  const srwDashMatch = lastSegment.match(/^(\d+)x(\d+)x$/i);
  if (srwDashMatch) {
    return "100";
  }

  // If the last segment matches any protocol case, suggest "+" to chain
  const matchedCase = detectProtocolCase(lastSegment);
  if (matchedCase) {
    return " + ";
  }

  // Pattern-based suggestions
  const cases = [
    // Case Ramp
    { regex: /^r$/, suggestion: "amp 3x4 @7" },
    { regex: /^ra$/, suggestion: "mp 3x4 @7" },
    { regex: /^ram$/, suggestion: "p 3x4 @7" },
    { regex: /^ramp$/, suggestion: " 3x4 @7" },
    { regex: /^ramp\s*$/, suggestion: "3x4 @7" },
    { regex: /^ramp\s*\d+$/, suggestion: "x4 @7" },
    { regex: /^ramp\s*\d+x$/, suggestion: "4 @7" },
    { regex: /^ramp\s*\d+x\d+$/, suggestion: " @7" },
    { regex: /^ramp\s*\d+x\d+\s*$/, suggestion: "@7" },
    { regex: /^ramp\s*\d+x\d+\s*@$/, suggestion: "7" },
    // Case SERIESxREPS @RPE
    { regex: /^\d+$/, suggestion: "x4 @5" },
    { regex: /^\d+x$/, suggestion: "4 @5" },
    { regex: /^\d+x\d+$/, suggestion: " @5" },
    { regex: /^\d+x\d+\s*$/, suggestion: "@5" },
    { regex: /^\d+x\d+\s*@$/, suggestion: "5" },
    // Case SINGLE @RPE
    { regex: /^s$/, suggestion: "ingle @5" },
    { regex: /^si$/, suggestion: "ngle @5" },
    { regex: /^sin$/, suggestion: "gle @5" },
    { regex: /^sing$/, suggestion: "le @5" },
    { regex: /^singl$/, suggestion: "e @5" },
    { regex: /^single$/, suggestion: " @5" },
    { regex: /^single\s*$/, suggestion: "@5" },
    { regex: /^single\s*@$/, suggestion: "5" },
    // Case xREPS @RPE
    { regex: /^x$/, suggestion: "4 @5" },
    { regex: /^x\d+$/, suggestion: " @5" },
    { regex: /^x\d+\s*$/, suggestion: "@5" },
    { regex: /^x\d+\s*@$/, suggestion: "5" },
    // Case + RAMP
    { regex: /\+\s*r$/, suggestion: "amp 3x4 @4" },
    { regex: /\+\s*ra$/, suggestion: "mp 3x4 @4" },
    { regex: /\+\s*ram$/, suggestion: "p 3x4 @4" },
    { regex: /\+\s*ramp$/, suggestion: " 3x4 @4" },
    { regex: /\+\s*ramp\s*$/, suggestion: "3x4 @4" },
    { regex: /\+\s*ramp\s*\d+$/, suggestion: "x4 @4" },
    { regex: /\+\s*ramp\s*\d+x$/, suggestion: "4 @4" },
    { regex: /\+\s*ramp\s*\d+x\d+$/, suggestion: " @4" },
    { regex: /\+\s*ramp\s*\d+x\d+\s*$/, suggestion: "@4" },
    { regex: /\+\s*ramp\s*\d+x\d+\s*@$/, suggestion: "4" },
    // Case + SERIESxREPS @RPE
    { regex: /\+$/, suggestion: " 3x4 @5" },
    { regex: /\+\s*$/, suggestion: "3x4 @5" },
    { regex: /\+\s*\d+$/, suggestion: "x4 @5" },
    { regex: /\+\s*\d+x$/, suggestion: "4 @5" },
    { regex: /\+\s*\d+x\d+$/, suggestion: " @5" },
    { regex: /\+\s*\d+x\d+\s*$/, suggestion: "@5" },
    { regex: /\+\s*\d+x\d+\s*@$/, suggestion: "5" },
    // Case + SINGLE @RPE
    { regex: /\+\s*s$/, suggestion: "ingle @5" },
    { regex: /\+\s*si$/, suggestion: "ngle @5" },
    { regex: /\+\s*sin$/, suggestion: "gle @5" },
    { regex: /\+\s*sing$/, suggestion: "le @5" },
    { regex: /\+\s*singl$/, suggestion: "e @5" },
    { regex: /\+\s*single$/, suggestion: " @5" },
    { regex: /\+\s*single\s*$/, suggestion: "@5" },
    { regex: /\+\s*single\s*@$/, suggestion: "5" },
    // Case weight progression (e.g. "120-125x6")
    { regex: /^\d+(?:-\d+)+$/, suggestion: " x4" },
    { regex: /^\d+(?:-\d+)+\s*$/, suggestion: "x4" },
    { regex: /^\d+(?:-\d+)+\s*x$/, suggestion: "4" },
    // Case + xREPS @RPE
    { regex: /\+\s*x$/, suggestion: "4 @5" },
    { regex: /\+\s*x\d+$/, suggestion: " @5" },
    { regex: /\+\s*x\d+\s*$/, suggestion: "@5" },
    { regex: /\+\s*x\d+\s*@$/, suggestion: "5" },
  ];

  for (let { regex, suggestion } of cases) {
    if (regex.test(collapsedSegment.toLowerCase())) {
      return suggestion;
    }
  }

  return "";
}

/**
 * @typedef {Object} ParsedSerieDefinition
 * @property {number} order - The order/index of the serie
 * @property {number|string} targetReps - Target reps value
 * @property {number|string} targetRpe - Target RPE value (empty string if using weight)
 * @property {number|string} targetWeight - Target weight value (empty string if using RPE)
 * @property {number} relevance - Priority level (1=high, 2=medium, 3=low)
 * @property {boolean} requireVideo - Whether video is required for this set
 */

/**
 * @typedef {Object} ParsedProtocolResult
 * @property {ParsedSerieDefinition[]} series - Array of parsed serie definitions
 * @property {boolean} success - Whether parsing was successful
 * @property {string|null} error - Error message if parsing failed
 */

/**
 * Parses a single protocol segment and returns serie definitions
 *
 * @param {string} protocolCase - The protocol case type (from ProtocolCases)
 * @param {string} text - The protocol text segment to parse
 * @param {number} startIndex - Starting index for serie order (for chaining)
 * @returns {ParsedSerieDefinition[]} Array of parsed serie definitions
 */
function parseProtocolSegment(protocolCase, text, startIndex = 0) {
  const normalized = normalizeText(text);
  const series = [];

  let match;
  let sets = 0;
  let reps = "";
  let rpe = "";
  let rampRpe = 0;
  let relevance = 3;
  let weightList = [];
  let progressionList = [];

  if (protocolCase === ProtocolCases.RPE_PROGRESSION) {
    // x<reps>@<rpe>-... progression, e.g. "x3@4-4-5"
    match = normalized.match(/^x(\d+)@((?:\d+(\.\d+)?)(?:-\d+(\.\d+)?)+)$/);
    if (!match) return series;
    reps = parseInt(match[1], 10);
    progressionList = match[2].split("-").map((n) => clampRpe(parseFloat(n)));
    sets = progressionList.length;
  } else if (protocolCase === ProtocolCases.WEIGHT_PROGRESSION) {
    // <w1>-<w2>-...x<reps>, e.g. "175-180-185x3"
    match = normalized.match(/^(\d+(?:-\d+)+)x(\d+)$/);
    if (!match) return series;
    reps = parseInt(match[2], 10);
    weightList = match[1].split("-").map((n) => Math.min(parseFloat(n), 999));
    sets = weightList.length;
    rpe = "";
  } else if (protocolCase === ProtocolCases.SETS_REPS_WEIGHT) {
    // <sets>x<reps>x<weight>, e.g. "3x5x145"
    match = normalized.match(/^(\d+)x(\d+)x(\d+(?:\.\d+)?)$/);
    if (!match) return series;
    sets = parseInt(match[1], 10);
    reps = parseInt(match[2], 10);
    const rawWeight = parseFloat(match[3]);
    const weightValue = Math.min(rawWeight, 999);
    weightList = Array(sets).fill(weightValue);
    rpe = "";
  } else if (protocolCase === ProtocolCases.RAMP) {
    // ramp <sets>x<reps>@<rpe>, e.g. "ramp3x4@8"
    match = normalized.match(/^ramp(\d+)x(\d+)@(\d+(\.\d+)?)/);
    if (!match) return series;
    sets = parseInt(match[1], 10);
    reps = match[2];
    rpe = match[3];
    rampRpe = clampRpe(parseFloat(rpe));
  } else if (protocolCase === ProtocolCases.SETS_REPS_RPE) {
    // <sets>x<reps>@<rpe>, e.g. "3x4@5"
    match = normalized.match(/^(\d+)x(\d+)@(\d+(\.\d+)?)/);
    if (!match) return series;
    sets = Math.max(parseInt(match[1], 10), 1);
    reps = match[2];
    rpe = clampRpe(parseFloat(match[3])).toString();
  } else if (protocolCase === ProtocolCases.SINGLE) {
    // single@<rpe>, e.g. "single@5"
    match = normalized.match(/@(\d+(\.\d+)?)/);
    if (!match) return series;
    sets = 1;
    reps = 1;
    rpe = clampRpe(parseFloat(match[1])).toString();
    relevance = 2;
  } else if (protocolCase === ProtocolCases.REPS_AND_RPE) {
    // x<reps>@<rpe>, e.g. "x4@5"
    match = normalized.match(/^x(\d+)@(\d+(\.\d+)?)/);
    if (!match) return series;
    sets = 1;
    reps = match[1];
    rpe = clampRpe(parseFloat(match[2])).toString();
    relevance = 1;
  } else {
    // Unknown case
    return series;
  }

  // Build series definitions
  if (sets === 0) {
    // Ensure at least one serie
    const serieDef = {
      order: startIndex,
      targetReps: reps,
      targetRpe:
        protocolCase === ProtocolCases.RPE_PROGRESSION
          ? progressionList[0]
          : rpe,
      targetWeight:
        protocolCase === ProtocolCases.WEIGHT_PROGRESSION ||
        protocolCase === ProtocolCases.SETS_REPS_WEIGHT
          ? weightList[0]
          : "",
      relevance:
        protocolCase === ProtocolCases.RAMP
          ? Math.min(sets, 3)
          : protocolCase === ProtocolCases.SINGLE ||
            protocolCase === ProtocolCases.REPS_AND_RPE
          ? relevance
          : 3,
      requireVideo:
        protocolCase === ProtocolCases.SINGLE ||
        protocolCase === ProtocolCases.REPS_AND_RPE,
    };
    series.push(serieDef);
    return series;
  }

  const startingRpe = rampRpe - (sets - 1);

  for (let i = 0; i < sets; i++) {
    const rawRpe =
      protocolCase === ProtocolCases.RAMP
        ? startingRpe + i
        : protocolCase === ProtocolCases.RPE_PROGRESSION
        ? progressionList[i]
        : rpe;
    const serieRpe = typeof rawRpe === "number" ? clampRpe(rawRpe) : rawRpe;

    const rawRelevance =
      protocolCase === ProtocolCases.RAMP
        ? sets - i
        : protocolCase === ProtocolCases.SINGLE ||
          protocolCase === ProtocolCases.REPS_AND_RPE
        ? relevance
        : 3;
    const serieRelevance = Math.min(Math.max(rawRelevance, 1), 3);

    const repsValue = Math.min(
      typeof reps === "number" ? reps : parseInt(reps, 10),
      999
    );

    const serieDef = {
      order: startIndex + i,
      targetReps: repsValue,
      targetRpe:
        protocolCase === ProtocolCases.WEIGHT_PROGRESSION ||
        protocolCase === ProtocolCases.SETS_REPS_WEIGHT
          ? ""
          : serieRpe,
      targetWeight:
        protocolCase === ProtocolCases.WEIGHT_PROGRESSION ||
        protocolCase === ProtocolCases.SETS_REPS_WEIGHT
          ? weightList[i]
          : "",
      relevance: serieRelevance,
      requireVideo:
        (protocolCase === ProtocolCases.RAMP && serieRelevance === 1) ||
        protocolCase === ProtocolCases.SINGLE ||
        protocolCase === ProtocolCases.REPS_AND_RPE,
    };

    series.push(serieDef);
  }

  return series;
}

/**
 * Parses a complete protocol text (including chained segments) and returns serie definitions
 *
 * @param {string} protocolText - The complete protocol text to parse
 * @param {number} maxSeries - Maximum number of series allowed (default 19)
 * @returns {ParsedProtocolResult} Object containing parsed series and success status
 *
 * @example
 * parseProtocolText("3x4 @5")
 * // Returns: { series: [...], success: true, error: null }
 *
 * parseProtocolText("ramp 3x4 @7 + single @8")
 * // Returns: { series: [...7 series...], success: true, error: null }
 *
 * parseProtocolText("invalid")
 * // Returns: { series: [], success: false, error: "Unknown protocol format" }
 */
export function parseProtocolText(protocolText, maxSeries = 19) {
  if (!protocolText || !protocolText.trim()) {
    return { series: [], success: true, error: null };
  }

  const text = protocolText.trim();
  const allSeries = [];

  // Split by "+" for chained protocols
  const segments = text
    .split("+")
    .map((s) => s.trim())
    .filter((s) => s);

  for (const segment of segments) {
    const protocolCase = detectProtocolCase(segment);

    if (!protocolCase) {
      // Note: Error message should be translated by the caller using the key "unknownProtocolFormat-string"
      // with {{segment}} variable. For now, keeping English for backward compatibility.
      return {
        series: [],
        success: false,
        error: `Unknown protocol format: "${segment}"`,
        errorKey: "unknownProtocolFormat-string",
        errorVariables: { segment },
      };
    }

    const currentIndex = allSeries.length;
    const remainingCapacity = maxSeries - currentIndex;

    if (remainingCapacity <= 0) {
      break; // Max series reached
    }

    const parsedSeries = parseProtocolSegment(
      protocolCase,
      segment,
      currentIndex
    );

    // Limit to remaining capacity
    const seriesToAdd = parsedSeries.slice(0, remainingCapacity);
    allSeries.push(...seriesToAdd);
  }

  return { series: allSeries, success: true, error: null };
}

/**
 * Applies parsed protocol definitions to an exercise by creating Serie objects
 *
 * This function is designed to be used with a Serie class constructor.
 * It handles the creation of new series while optionally preserving
 * existing "protocolSeries" data for series that had custom configurations.
 *
 * @param {Object} exercise - The exercise object to apply series to
 * @param {ParsedSerieDefinition[]} seriesDefinitions - Array of parsed serie definitions
 * @param {Function} SerieClass - The Serie class constructor
 * @param {Object} options - Optional configuration
 * @param {boolean} options.preserveProtocolSeries - Whether to preserve existing protocolSeries values (default true)
 * @param {boolean} options.clearExistingSeries - Whether to clear existing series first (default true)
 *
 * @example
 * import Serie from "../Firestore/Serie";
 * const parsed = parseProtocolText("3x4 @5");
 * applyProtocolSeriesToExercise(exercise, parsed.series, Serie);
 */
export function applyProtocolSeriesToExercise(
  exercise,
  seriesDefinitions,
  SerieClass,
  options = {}
) {
  const { preserveProtocolSeries = true, clearExistingSeries = true } = options;

  if (!exercise || !seriesDefinitions || !SerieClass) {
    return;
  }

  // Optionally backup existing series before clearing
  if (preserveProtocolSeries && clearExistingSeries) {
    const oldSeries = exercise.protocolSeries || [];
    const currentSeries = exercise.series || [];

    // Check if current series have meaningful data (not just defaults)
    const hasNonDefaultSeries = currentSeries.some(
      (serie) =>
        serie.relevance !== 3 ||
        serie.requireVideo ||
        serie.targetWeight !== "" ||
        serie.targetRpe !== "" ||
        serie.targetReps !== "" ||
        (serie.weight ?? 0) > 0 ||
        (serie.reps ?? 0) > 0 ||
        (serie.rpe ?? 0) > 0 ||
        serie.hasVideo !== undefined
    );

    if (hasNonDefaultSeries) {
      // Merge current series into protocolSeries backup
      const newProtocolSeries = [...oldSeries];
      for (let i = 0; i < currentSeries.length; i++) {
        newProtocolSeries[i] = currentSeries[i];
      }
      exercise.setProperty("protocolSeries", newProtocolSeries);
    }
  }

  if (clearExistingSeries) {
    exercise.setProperty("series", []);
  }

  // Create new series from definitions
  for (const def of seriesDefinitions) {
    const newSerie = new SerieClass(
      undefined,
      def.order,
      0, // reps (actual)
      0, // rpe (actual)
      0, // weight (actual)
      undefined, // targetReps (will be set below)
      undefined, // targetRpe (will be set below)
      undefined, // targetWeight (will be set below)
      exercise,
      undefined // relevance (will be set below)
    );

    // Check if we have preserved protocolSeries data for this index
    if (
      preserveProtocolSeries &&
      def.order < (exercise.protocolSeries?.length || 0)
    ) {
      const foundSerie = exercise.protocolSeries[def.order];
      if (foundSerie) {
        newSerie.targetWeight = foundSerie.targetWeight;
        newSerie.targetRpe = foundSerie.targetRpe;
        newSerie.targetReps = foundSerie.targetReps;
        newSerie.relevance = foundSerie.relevance;
        newSerie.requireVideo = foundSerie.requireVideo;
        newSerie.weight = foundSerie.weight ?? newSerie.weight;
        newSerie.reps = foundSerie.reps ?? newSerie.reps;
        newSerie.rpe = foundSerie.rpe ?? newSerie.rpe;
        if (foundSerie.hasVideo !== undefined) {
          newSerie.hasVideo = foundSerie.hasVideo;
        }
      }
    } else {
      // Apply values from the parsed definition
      newSerie.requireVideo = def.requireVideo;
      newSerie.relevance = def.relevance;
    }

    // Always apply target values from the protocol (unless they came from protocolSeries)
    if (def.targetReps && !newSerie.targetReps) {
      newSerie.targetReps = def.targetReps;
    }

    if (
      def.targetWeight !== "" &&
      def.targetWeight !== undefined &&
      !newSerie.targetWeight
    ) {
      newSerie.targetWeight = def.targetWeight;
    } else if (def.targetRpe !== "" && def.targetRpe !== undefined) {
      newSerie.targetRpe = def.targetRpe;
    }

    exercise.series.push(newSerie);
  }
}

/**
 * Checks if any series in the exercise have actual data (weight, reps, or rpe > 0)
 * This is used to warn users before overwriting data.
 *
 * @param {Object} exercise - The exercise object to check
 * @returns {boolean} True if any series has actual data
 *
 * @example
 * if (hasSeriesWithActualData(exercise) && !exercise.ignoreActualWithDataWarning) {
 *   showWarning();
 *   return;
 * }
 */
export function hasSeriesWithActualData(exercise) {
  if (!exercise?.series) return false;
  return exercise.series.some((s) => s.weight > 0 || s.rpe > 0 || s.reps > 0);
}

/**
 * Checks if a serie is meaningfully empty (has only default/empty values)
 * Reuses the hasValue logic from Exercise.hasSeriesWithAnyData
 *
 * @param {Object} serie - The serie object to check
 * @returns {boolean} True if the serie is meaningfully empty
 */
function isSerieMeaningfullyEmpty(serie) {
  if (!serie) return true;

  const hasValue = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed === "") return false;
      const numeric = Number(trimmed);
      if (Number.isFinite(numeric) && numeric === 0) return false;
      return true;
    }
    if (typeof value === "number") {
      return Number.isFinite(value) && value !== 0;
    }
    return false;
  };

  // Check if serie has any meaningful data (inverse of hasSeriesWithAnyData logic)
  if (serie.hasVideo === true || serie.requireVideo === true) return false;
  if (typeof serie.relevance === "number" && serie.relevance !== 3)
    return false;
  if (
    hasValue(serie.targetWeight) ||
    hasValue(serie.targetReps) ||
    hasValue(serie.targetRpe) ||
    hasValue(serie.weight) ||
    hasValue(serie.reps) ||
    hasValue(serie.rpe)
  ) {
    return false;
  }

  return true; // Serie is meaningfully empty
}

/**
 * Full protocol update function that handles the complete flow:
 * 1. Backs up existing series to protocolSeries (preserving manual edits)
 * 2. Parses the protocol text
 * 3. Updates existing series in place if they have no actual data, or creates new ones
 *
 * This is the main function to call when protocol text changes.
 *
 * @param {Object} exercise - The exercise object to update
 * @param {string} protocolText - The protocol text to apply
 * @param {Function} SerieClass - The Serie class constructor
 * @param {Object} options - Optional configuration
 * @param {number} options.maxSeries - Maximum number of series allowed (default 19)
 *
 * @example
 * import Serie from "../Firestore/Serie";
 *
 * // When protocol text changes:
 * updateExerciseWithProtocol(slotExercise, "3x4 @5 + single @7", Serie);
 */
export function updateExerciseWithProtocol(
  exercise,
  protocolText,
  SerieClass,
  options = {}
) {
  const { maxSeries = 19 } = options;

  if (!exercise || !SerieClass) return;

  const text = protocolText || "";

  // Parse the protocol text
  const parsed = parseProtocolText(text, maxSeries);

  if (!parsed.success || parsed.series.length === 0) {
    // If parsing fails or no series, clear the series
    if (text.trim() === "") {
      // Guard: Don't delete sets if exercise already has sets (prevent deleting last remaining sets)
      const existingSeries = exercise.series || [];
      if (existingSeries.length > 0) {
        // Keep existing sets intact, just return without clearing
        return;
      }
      // Back up existing series before clearing (if they have meaningful data)
      backupSeriesToProtocolSeries(exercise);
      exercise.setProperty("series", []);
    }
    return;
  }

  const existingSeries = exercise.series || [];
  const existingSeriesCount = existingSeries.length;
  const parsedSeries = parsed.series;

  // Check if existing series have actual data (weight, reps, or rpe > 0)
  const hasActualData = existingSeries.some((serie) => {
    if (!serie) return false;
    const hasWeight = (serie.weight ?? 0) > 0;
    const hasReps = (serie.reps ?? 0) > 0;
    const hasRpe = (serie.rpe ?? 0) > 0;
    return hasWeight || hasReps || hasRpe;
  });

  // Check if sets are completely blank (no actual data AND no meaningful configuration)
  // If completely blank, use original logic (set all properties from protocol)
  // If not completely blank but no actual data, update in place (only protocol-modified properties)
  const areSetsCompletelyBlank =
    existingSeries.length === 0 ||
    existingSeries.every((serie) => {
      if (!serie) return true;
      const hasActual =
        (serie.weight ?? 0) > 0 ||
        (serie.reps ?? 0) > 0 ||
        (serie.rpe ?? 0) > 0;
      // Check if serie has any meaningful configuration
      const hasTargetWeight = (serie.targetWeight ?? "") !== "";
      const hasTargetRpe = (serie.targetRpe ?? "") !== "";
      const hasTargetReps = (serie.targetReps ?? "") !== "";
      const hasCustomRelevance =
        serie.relevance !== undefined && serie.relevance !== 3;
      const hasRequireVideo = serie.requireVideo === true;
      return (
        !hasActual &&
        !hasTargetWeight &&
        !hasTargetRpe &&
        !hasTargetReps &&
        !hasCustomRelevance &&
        !hasRequireVideo
      );
    });

  // If sets are not completely blank and count matches, update in place (only protocol-modified properties)
  // If sets are completely blank, use original logic (clear and recreate with all properties)
  const canUpdateInPlace =
    !areSetsCompletelyBlank &&
    !hasActualData &&
    existingSeries.length === parsedSeries.length &&
    existingSeries.length > 0;

  if (canUpdateInPlace) {
    // Update existing series in place - only modify properties that the protocol sets
    for (let i = 0; i < parsedSeries.length; i++) {
      const def = parsedSeries[i];
      const existingSerie = existingSeries[i];

      if (!existingSerie) continue;

      // Determine which properties this protocol modifies
      // Weight-based protocols (WEIGHT_PROGRESSION, SETS_REPS_WEIGHT): modify targetWeight + targetReps
      // RPE-based protocols (others): modify targetRpe + targetReps
      const isWeightBased =
        def.targetWeight !== "" && def.targetWeight !== undefined;
      const isRpeBased = def.targetRpe !== "" && def.targetRpe !== undefined;

      // Always update targetReps (all protocols set this)
      if (def.targetReps !== undefined && def.targetReps !== null) {
        existingSerie.targetReps = def.targetReps;
      }

      if (isWeightBased) {
        // Weight-based protocol: update targetWeight + targetReps
        existingSerie.targetWeight = def.targetWeight;
        // // Clear targetRpe when using weight (protocols are mutually exclusive)
        // if (existingSerie.targetRpe !== "") {
        //   existingSerie.targetRpe = "";
        // }
      } else if (isRpeBased) {
        // RPE-based protocol: update targetRpe + targetReps
        existingSerie.targetRpe = def.targetRpe;
        // // Clear targetWeight when using RPE (protocols are mutually exclusive)
        // if (existingSerie.targetWeight !== "") {
        //   existingSerie.targetWeight = "";
        // }
      }

      // Don't update relevance or requireVideo when sets have actual data
      // Keep existing values to preserve user's configuration
    }
    // Toggle updateSets flag to trigger reactions in components
    exercise.updateSets = !exercise.updateSets;
  } else {
    // Original logic: sets are blank, so set all properties from protocol
    // Back up existing series before clearing
    backupSeriesToProtocolSeries(exercise);

    // Clear existing series
    exercise.setProperty("series", []);

    // Create new series from parsed definitions
    for (const def of parsedSeries) {
      const newSerie = new SerieClass(
        undefined,
        def.order,
        0, // reps (actual)
        0, // rpe (actual)
        0, // weight (actual)
        undefined, // targetReps
        undefined, // targetRpe
        undefined, // targetWeight
        exercise,
        undefined // relevance
      );

      // Check if we have preserved protocolSeries data for this index
      // Restore from protocolSeries if data exists for this index, regardless of previous series count
      // This allows restoring sets that were backed up earlier even when protocol count changed
      const protocolSeries = exercise.protocolSeries || [];
      const shouldUseProtocolSeries = def.order < protocolSeries.length;
      if (shouldUseProtocolSeries) {
        const foundSerie = protocolSeries[def.order];
        if (foundSerie) {
          // Check if protocolSeries data is meaningfully empty
          if (isSerieMeaningfullyEmpty(foundSerie)) {
            // Apply relevance and requireVideo from protocol
            newSerie.relevance = def.relevance;
            newSerie.requireVideo = def.requireVideo;
          } else {
            // Preserve non-protocol properties from protocolSeries
            newSerie.relevance = foundSerie.relevance;
            newSerie.requireVideo = foundSerie.requireVideo;
            // Restore target values from protocolSeries to preserve manual edits
            newSerie.targetWeight = foundSerie.targetWeight ?? "";
            newSerie.targetRpe = foundSerie.targetRpe ?? "";
            newSerie.targetReps = foundSerie.targetReps ?? "";
          }
          // Always preserve actual data and hasVideo
          newSerie.weight = foundSerie.weight ?? newSerie.weight;
          newSerie.reps = foundSerie.reps ?? newSerie.reps;
          newSerie.rpe = foundSerie.rpe ?? newSerie.rpe;
          if (foundSerie.hasVideo !== undefined) {
            newSerie.hasVideo = foundSerie.hasVideo;
          }
        }
      } else {
        // No preserved data - apply values from the parsed definition
        newSerie.requireVideo = def.requireVideo;
        newSerie.relevance = def.relevance;
      }

      // Determine which properties this protocol modifies
      // Weight-based protocols (WEIGHT_PROGRESSION, SETS_REPS_WEIGHT): modify targetWeight + targetReps
      // RPE-based protocols (others): modify targetRpe + targetReps
      const isWeightBased =
        def.targetWeight !== "" && def.targetWeight !== undefined;
      const isRpeBased = def.targetRpe !== "" && def.targetRpe !== undefined;

      // Apply target values from the protocol - only override properties that the protocol modifies
      // Always update targetReps (all protocols set this)
      if (def.targetReps !== undefined && def.targetReps !== null) {
        newSerie.targetReps = def.targetReps;
      }

      if (isWeightBased) {
        // Weight-based protocol: update targetWeight + targetReps
        // Preserve targetRpe if it exists (protocols are not mutually exclusive)
        newSerie.targetWeight = def.targetWeight;
      } else if (isRpeBased) {
        // RPE-based protocol: update targetRpe + targetReps
        // Preserve targetWeight if it exists (protocols are not mutually exclusive)
        newSerie.targetRpe = def.targetRpe;
      }

      exercise.series.push(newSerie);
    }
    // Toggle updateSets flag to trigger reactions in components
    exercise.updateSets = !exercise.updateSets;
  }
}

/**
 * Backs up existing series to protocolSeries for preservation
 * This allows manual edits to be restored when the protocol text changes
 *
 * @param {Object} exercise - The exercise object
 */
function backupSeriesToProtocolSeries(exercise) {
  if (!exercise) return;

  const currentSeries = exercise.series || [];
  const oldProtocolSeries = exercise.protocolSeries || [];

  // Check if current series have meaningful data (not just defaults)
  const hasNonDefaultSeries = currentSeries.some(
    (serie) =>
      serie.relevance !== 3 ||
      serie.requireVideo ||
      serie.targetWeight !== "" ||
      serie.targetRpe !== "" ||
      serie.targetReps !== "" ||
      (serie.weight ?? 0) > 0 ||
      (serie.reps ?? 0) > 0 ||
      (serie.rpe ?? 0) > 0 ||
      serie.hasVideo !== undefined
  );

  if (hasNonDefaultSeries) {
    // Merge current series into protocolSeries backup
    // This preserves higher indices from oldProtocolSeries when currentSeries has fewer items
    // Example: If oldProtocolSeries has indices [0,1,2] and currentSeries has [0,1],
    // the result will be [currentSeries[0], currentSeries[1], oldProtocolSeries[2]]
    // This allows sets to be restored later when protocol expands again
    const newProtocolSeries = [...oldProtocolSeries];
    for (let i = 0; i < currentSeries.length; i++) {
      newProtocolSeries[i] = currentSeries[i];
    }
    exercise.setProperty("protocolSeries", newProtocolSeries);
  }
}

/**
 * Creates a protocol handler object that can be used to manage protocol text state
 * This is useful when you need to integrate protocol text functionality into a component
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.onProtocolChange - Callback when protocol text changes
 * @param {Function} options.onSuggestionAccept - Callback when a suggestion is accepted
 * @returns {Object} Protocol handler with methods for managing protocol text
 */
export function createProtocolTextHandler(options = {}) {
  const { onProtocolChange, onSuggestionAccept } = options;

  return {
    /**
     * Gets the current suggestion for a protocol text
     */
    getSuggestion: getProtocolSuggestion,

    /**
     * Validates protocol text
     */
    hasError: hasProtocolTextError,

    /**
     * Parses protocol text into series definitions
     */
    parse: parseProtocolText,

    /**
     * Handles text change with validation and callbacks
     */
    handleTextChange(text, exercise) {
      const hasError = hasProtocolTextError(text);
      if (onProtocolChange) {
        onProtocolChange(text, hasError, exercise);
      }
      return { text, hasError };
    },

    /**
     * Accepts the current suggestion and appends it to the text
     */
    acceptSuggestion(currentText) {
      const suggestion = getProtocolSuggestion(currentText);
      if (suggestion) {
        const newText = currentText + suggestion;
        if (onSuggestionAccept) {
          onSuggestionAccept(newText, suggestion);
        }
        return newText;
      }
      return currentText;
    },
  };
}

/**
 * Handles key press events for protocol text input
 * Processes Tab/Enter to accept suggestions
 *
 * @param {Object} params - Parameters
 * @param {Object} params.event - The keyboard event (nativeEvent)
 * @param {string} params.currentText - Current protocol text
 * @param {Function} params.onAcceptSuggestion - Callback when suggestion is accepted, receives (newText)
 * @param {Function} params.onExitEdit - Callback when user wants to exit edit mode
 * @param {Function} params.preventDefault - Function to prevent default event behavior
 * @returns {boolean} True if the event was handled
 *
 * @example
 * const handleKeyPress = (e) => {
 *   const handled = handleProtocolKeyPress({
 *     event: e.nativeEvent,
 *     currentText: protocolText,
 *     onAcceptSuggestion: (newText) => {
 *       setProtocolText(newText);
 *       updateExerciseWithProtocol(exercise, newText, Serie);
 *     },
 *     onExitEdit: () => setShowingEdit(false),
 *     preventDefault: () => e.preventDefault(),
 *   });
 * };
 */
export function handleProtocolKeyPress({
  event,
  currentText,
  onAcceptSuggestion,
  onExitEdit,
  preventDefault,
}) {
  const key = event?.key;
  if (key !== "Enter" && key !== "Tab") {
    return false;
  }

  const suggestion = getProtocolSuggestion(currentText);

  if (suggestion) {
    // If suggestion is " + " and user pressed Enter, exit edit mode instead
    if (suggestion === " + " && key === "Enter") {
      if (preventDefault) preventDefault();
      if (onExitEdit) onExitEdit();
      return true;
    }

    // Accept the suggestion
    const newText = currentText + suggestion;
    if (onAcceptSuggestion) {
      onAcceptSuggestion(newText);
    }
    if (preventDefault) preventDefault();
    return true;
  } else if (currentText !== "") {
    // No suggestion and text is not empty - exit edit mode
    if (onExitEdit) onExitEdit();
    if (preventDefault) preventDefault();
    return true;
  }

  return false;
}

/**
 * Complete protocol text change handler that includes warning check
 * and series update. This is the main function to use when handling
 * text input changes in a protocol text field.
 *
 * @param {Object} params - Parameters
 * @param {string} params.text - New protocol text
 * @param {Object} params.exercise - The exercise object
 * @param {Function} params.SerieClass - The Serie class constructor
 * @param {Function} params.onShowWarning - Callback to show warning dialog
 * @param {boolean} params.checkActualData - Whether to check for actual data warning (default true)
 * @returns {boolean} True if the change was applied, false if warning was shown
 *
 * @example
 * const handleTextChange = (text) => {
 *   const applied = handleProtocolTextChange({
 *     text,
 *     exercise: slotExercise,
 *     SerieClass: Serie,
 *     onShowWarning: () => slotExercise.setProperty("showActualWithDataWarning", true),
 *   });
 *   if (applied) {
 *     // Update UI state
 *   }
 * };
 */
export function handleProtocolTextChange({
  text,
  exercise,
  SerieClass,
  onShowWarning,
  checkActualData = true,
}) {
  if (!exercise) return false;

  // Check if any series have actual data
  if (checkActualData) {
    const actualWithData = hasSeriesWithActualData(exercise);

    if (
      actualWithData &&
      !exercise.ignoreActualWithDataWarning &&
      exercise.workout
    ) {
      if (onShowWarning) onShowWarning();
      return false;
    }
  }

  // Update the protocol text
  exercise.setProperty("protocolText", text);

  // Update the series
  updateExerciseWithProtocol(exercise, text, SerieClass);

  return true;
}

export default {
  matchProtocolCase,
  detectProtocolCase,
  hasProtocolTextError,
  getProtocolSuggestion,
  parseProtocolText,
  applyProtocolSeriesToExercise,
  updateExerciseWithProtocol,
  hasSeriesWithActualData,
  handleProtocolKeyPress,
  handleProtocolTextChange,
  createProtocolTextHandler,
  ProtocolCases,
};
